import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Button,
  Box,
  Pagination,
  CircularProgress,
  Alert,
  Grid,
  Container,
} from "@mui/material";
import useUserId from "hooks/useUserId";
import useCarnivalWebSocket from "hooks/useCarnivalWebSocket";
import axios from "axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import SearchBarCarnivals from "../SearchBarCarnivals";
import CandidateCard from "./CandidateCard";
import SharedCarnivalView from "./SharedCarnivalView";

// Main styles with midnight blue theme
const styles = {
  mainContainer: { bgcolor: "#f8fafc", minHeight: "100vh", py: 4 },
  pageTitle: {
    mb: 4,
    color: "#0017E7",
    fontWeight: 700,
    textAlign: "center",
    background: "linear-gradient(45deg, #0017E7, #1e40af)",
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    mt: 4,
    "& .MuiPaginationItem-root": {
      color: "#0017E7",
      borderRadius: 2,
    },
    "& .Mui-selected": {
      bgcolor: "#0017E7 !important",
      color: "#ffffff",
    },
  },
  primaryButton: {
    background: "linear-gradient(45deg, #0017E7 30%, #1e40af 90%)",
    "&:hover": {
      background: "linear-gradient(45deg, #0010a3 30%, #1e3a8a 90%)",
      transform: "translateY(-1px)",
    },
    borderRadius: 2,
    boxShadow: "0 2px 4px rgba(0, 23, 231, 0.2)",
    transition: "all 0.2s ease-in-out",
  },
  outlineButton: {
    borderColor: "#0017E7",
    color: "#0017E7",
    borderRadius: 2,
    "&:hover": {
      borderColor: "#0010a3",
      bgcolor: "rgba(0, 23, 231, 0.04)",
    },
  },
};

const CandidateDashboard = () => {
  const [carnivals, setCarnivals] = useState([]);
  const [myTickets, setMyTickets] = useState([]);
  const [fileStates, setFileStates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL;
  const { userId } = useUserId();
  const navigate = useNavigate();
  const { carnivalId } = useParams();
  const location = useLocation();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [searchResults, setSearchResults] = useState(null);
  const [isSearchMode, setIsSearchMode] = useState(false);

  // WebSocket state for real-time updates
  const [carnivalUpdates, setCarnivalUpdates] = useState({});
  const [queueUpdates, setQueueUpdates] = useState({});

  // Determine if we're viewing a shared carnival
  const isSharedView = Boolean(carnivalId);
  const isAuthenticated = Boolean(localStorage.getItem("accessToken"));
  const sharedCarnivalId =
    carnivalId || new URLSearchParams(location.search).get("carnival");
  const isDeadlinePassed = (deadlineDate) => {
    const now = new Date();
    const deadline = new Date(deadlineDate);
    return now.getTime() > deadline.getTime();
  };

  useEffect(() => {
    // Check authentication first
    if (!isAuthenticated) {
      if (isSharedView) {
        // Save the carnival ID and redirect to login
        navigate(
          `/login?redirect=${encodeURIComponent(
            `/carnival/${sharedCarnivalId}`
          )}`
        );
        return;
      } else {
        navigate("/login");
        return;
      }
    }

    // Fetch appropriate data based on view mode
    if (isSharedView) {
      fetchSingleCarnival(sharedCarnivalId);
    } else if (!isSearchMode) {
      // Only fetch standard carnivals if not in search mode
      fetchCarnivals(currentPage);
      checkUser();
    } else {
      fetchCarnivals(currentPage);
      checkUser();
    }

    // Always fetch tickets if authenticated
    if (isAuthenticated) {
      fetchMyTickets();
    }
  }, [currentPage, sharedCarnivalId, isAuthenticated, isSharedView]);

  useEffect(() => {
    // Initial fetch
    if (isAuthenticated) {
      fetchMyTickets();
    }

    // Note: Removed polling interval - now using WebSocket for real-time updates
    // WebSocket will trigger fetchMyTickets() when queue changes occur
    // No need for periodic polling anymore
  }, [isAuthenticated, userId]);

  // WebSocket handlers for real-time updates
  const handleParticipantUpdate = useCallback((data) => {
    console.log("Participant update received:", data);
    setCarnivalUpdates((prev) => ({
      ...prev,
      [data.carnival_id]: {
        ...prev[data.carnival_id],
        current_participants: data.current_participants,
        max_participants: data.max_participants,
        lastUpdate: new Date().toISOString(),
      },
    }));

    // Update the carnivals list with new participant count
    setCarnivals((prevCarnivals) =>
      prevCarnivals.map((carnival) =>
        carnival.id === data.carnival_id
          ? { ...carnival, current_participants: data.current_participants }
          : carnival
      )
    );
  }, []);

  const handleQueueUpdate = useCallback(
    (data) => {
      console.log("Queue update received:", data);
      setQueueUpdates((prev) => ({
        ...prev,
        [data.carnival_id]: {
          ...prev[data.carnival_id],
          action: data.action,
          lastUpdate: new Date().toISOString(),
        },
      }));

      // Refresh tickets when queue changes
      if (isAuthenticated) {
        fetchMyTickets();
      }
    },
    [isAuthenticated]
  );

  const handleStatusChange = useCallback((data) => {
    console.log("Status change received:", data);
    setCarnivalUpdates((prev) => ({
      ...prev,
      [data.carnival_id]: {
        ...prev[data.carnival_id],
        is_active: data.is_active,
        is_archived: data.is_archived,
        lastUpdate: new Date().toISOString(),
      },
    }));

    // Update the carnivals list with new status
    setCarnivals((prevCarnivals) =>
      prevCarnivals.map((carnival) =>
        carnival.id === data.carnival_id
          ? {
              ...carnival,
              is_active: data.is_active,
              is_archived: data.is_archived,
            }
          : carnival
      )
    );
  }, []);

  // Handle search results
  const handleSearchResults = (results) => {
    setLoading(false);

    if (results === null) {
      // Reset to normal view
      setIsSearchMode(false);
      fetchCarnivals(currentPage);
    } else {
      // Update with search results
      setCarnivals(results);
      setIsSearchMode(true);
    }
  };

  const checkUser = async () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      navigate("/login");
      return;
    }

    try {
      const response = await axios.get(
        `${apiUrl}authentication/auth/validate-token/`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (!response.data.valid) {
        navigate("/login");
      }
    } catch (error) {
      console.error("Error validating token:", error);
      navigate("/login");
    }
  };

  const fetchSingleCarnival = async (encodedId) => {
    if (!isAuthenticated) return;

    try {
      setLoading(true);
      const decodedId = atob(encodedId);
      const response = await axios.get(
        `${apiUrl}opportunities/api/carnivals/${decodedId}/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      setCarnivals([response.data]);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching carnival:", error);
      setError(
        "Failed to load carnival information. The carnival may not exist or is no longer available."
      );
      setCarnivals([]);
      setLoading(false);
    }
  };

  const fetchCarnivals = async (page) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiUrl}opportunities/api/carnivals/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          params: { page, page_size: pageSize },
        }
      );

      setCarnivals(response.data.results || []);
      if (response.data.count) {
        setTotalPages(Math.ceil(response.data.count / pageSize));
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching carnivals:", error);
      setCarnivals([]);
      setLoading(false);
    }
  };

  const fetchMyTickets = async () => {
    try {
      const response = await axios.get(
        `${apiUrl}opportunities/api/interview-rooms/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          params: { candidate: userId },
        }
      );
      const tickets = response.data.results || response.data || [];
      setMyTickets(tickets);
    } catch (error) {
      console.error("Error fetching tickets:", error);
      setMyTickets([]);
    }
  };

  const handleFileSelect = (event, carnivalId) => {
    const file = event.target.files[0];
    setFileStates((prevState) => ({
      ...prevState,
      [carnivalId]: { selectedFile: file, status: "" },
    }));
  };

  const handleSelectExistingCV = (carnivalId, mockFile, resumePath) => {
    setFileStates((prevState) => ({
      ...prevState,
      [carnivalId]: { 
        selectedFile: mockFile, 
        status: "",
        selectedCVPath: resumePath
      },
    }));
  };

  const handleGetTicket = async (carnivalId) => {
    if (!isAuthenticated) {
      navigate(
        `/login?redirect=${encodeURIComponent(`/carnival/${carnivalId}`)}`
      );
      return;
    }

    const carnivalState = fileStates[carnivalId];
    if (!carnivalState?.selectedFile) {
      setFileStates((prevState) => ({
        ...prevState,
        [carnivalId]: { ...carnivalState, status: "Please select a CV first" },
      }));
      return;
    }

    let fileToUpload = carnivalState.selectedFile;

    // If it's an existing CV, fetch it from the server
    if (carnivalState.selectedFile.isExistingCV && carnivalState.selectedCVPath) {
      try {
        const normalizedResumePath = carnivalState.selectedCVPath.replace(/\\/g, "/");
        const fileURL = `${apiUrl}media/${normalizedResumePath}`;
        
        const response = await fetch(fileURL);
        if (!response.ok) {
          throw new Error("Failed to fetch CV file");
        }
        
        const blob = await response.blob();
        const fileName = carnivalState.selectedFile.name;
        
        // Create a proper File object from the blob
        fileToUpload = new File([blob], fileName, { type: blob.type });
      } catch (error) {
        console.error("Error fetching existing CV:", error);
        setFileStates((prevState) => ({
          ...prevState,
          [carnivalId]: {
            ...carnivalState,
            status: "Error loading selected CV",
          },
        }));
        return;
      }
    }

    const formData = new FormData();
    formData.append("carnival_id", carnivalId);
    formData.append("user_Id", userId);
    formData.append("cv", fileToUpload);

    try {
      const response = await axios.post(
        `${apiUrl}opportunities/api/interview-rooms/get_ticket/`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setMyTickets([...myTickets, response.data]);
      setFileStates((prevState) => ({
        ...prevState,
        [carnivalId]: {
          selectedFile: null,
          status: "Ticket created successfully!",
        },
      }));
    } catch (error) {
      console.error("Error getting ticket:", error);
      setFileStates((prevState) => ({
        ...prevState,
        [carnivalId]: {
          ...carnivalState,
          status: error.response?.data?.error || "Error creating ticket",
        },
      }));
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleLogin = () => {
    const returnUrl = sharedCarnivalId ? `/carnival/${sharedCarnivalId}` : "/";
    navigate(`/login?redirect=${encodeURIComponent(returnUrl)}`);
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
          bgcolor: "#f8faff",
        }}
      >
        <CircularProgress sx={{ color: "#2563eb" }} />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3, bgcolor: "#f8faff", minHeight: "100vh" }}>
        <Alert severity="error">{error}</Alert>
        <Button
          variant="contained"
          sx={styles.primaryButton}
          onClick={() => navigate("/")}
        >
          Go to Home
        </Button>
      </Box>
    );
  }

  // Single carnival shared view
  if (isSharedView && carnivals.length === 1) {
    const carnival = carnivals[0];
    const hasTicket =
      isAuthenticated &&
      myTickets.some(
        (ticket) =>
          ticket.carnival === carnival.id && ticket.candidate === userId
      );
    const ticket = myTickets.find((ticket) => ticket.carnival === carnival.id);
    const carnivalState = fileStates[carnival.id] || {};

    return (
      <SharedCarnivalView
        carnival={carnival}
        isAuthenticated={isAuthenticated}
        hasTicket={hasTicket}
        ticket={ticket}
        carnivalState={carnivalState}
        handleFileSelect={handleFileSelect}
        handleGetTicket={handleGetTicket}
        isDeadlinePassed={isDeadlinePassed}
        handleLogin={handleLogin}
        navigate={navigate}
      />
    );
  }

  // Regular dashboard view with multiple carnivals in a grid
  return (
    <Box sx={styles.mainContainer}>
      <Container maxWidth="xl">
        <Typography variant="h4" sx={styles.pageTitle}>
          caravanes disponibles
        </Typography>
        {/* Search bar component integration */}
        <SearchBarCarnivals
          onSearchResults={handleSearchResults}
          setLoading={setLoading}
        />

        {isSearchMode && (
          <Box
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body1" sx={{ color: "#475569" }}>
              {carnivals.length} results found
            </Typography>
            <Button
              variant="text"
              onClick={() => handleSearchResults(null)}
              sx={{ color: "#2563eb", textTransform: "none" }}
            >
              Clear search
            </Button>
          </Box>
        )}
        {carnivals.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              bgcolor: "#ffffff",
              borderRadius: 3,
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
            }}
          >
            <Typography variant="h6" sx={{ color: "#64748b" }}>
              No carnivals available at the moment.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {carnivals.map((carnival) => {
              const fileState = fileStates[carnival.id] || {};
              const hasTicket = myTickets.some(
                (ticket) =>
                  ticket.carnival === carnival.id && ticket.candidate === userId
              );
              const ticket = myTickets.find(
                (ticket) => ticket.carnival === carnival.id
              );

              return (
                <Grid item xs={12} sm={6} md={4} key={carnival.id}>
                  <CandidateCard
                    carnival={carnival}
                    userId={userId}
                    fileState={fileState}
                    hasTicket={hasTicket}
                    ticket={ticket}
                    onFileSelect={handleFileSelect}
                    onGetTicket={handleGetTicket}
                    isDeadlinePassed={isDeadlinePassed}
                    onSelectExistingCV={handleSelectExistingCV}
                    showSnackbar={(message) => console.log(message)}
                  />
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Pagination component - only show in normal view with multiple pages */}
        {!isSharedView && totalPages > 1 && (
          <Box sx={styles.pagination}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default CandidateDashboard;
